import {trans, API} from '@utils'

export const _fillAdvancedFilterValue = (value, optionsData) => {
  if (_.size(value) > 0) {
    const {dynamic_filter} = value
    const _value = {}
    if (_.size(dynamic_filter) > 0) {
      _.each(dynamic_filter, ({key, value, label, operator, not}) => {
        const options = _.get(optionsData, key, _.map(label, (label, index) => ({ value: value[index], label }))) 
        _value[key] = {operator, value, not}
        if (_.size(options) > 0) {
          _value[key].options = options
        }
      })
    }
    return _value
  }
  return {}
}

export const _loadSimpleOptions = ({ q = '', ids = [], remoteUrl, remoteUrlFormatter, extraRemoteParams }) => {
  return new Promise((resolve, reject) => {
    const params = { limit: 20, q, ...extraRemoteParams}
    if (_.size(ids)) params.ids = ids
    API.get(remoteUrl, {
      params
    }).then(res => {
      const data = _.map(res.data, (item) => remoteUrlFormatter(item))
      resolve(data)
    })
  })
}

export const _loadAdvancedSelectedOptionsWithRelationFilterKeys = (value, filters) => {
  if (_.isNil(value)) {
    return Promise.resolve({ optionsData: {}, relationFilterKeys: {} })
  }
  const valueDynamicFilter = _.keyBy(value.dynamic_filter, 'key')
  const filtersHasRelation = _.filter(filters, filter => _.size(filter.relations) > 0)
  const filterskeyByValue = _.keyBy(filtersHasRelation, 'value')
  const promises = []
  _.each(filterskeyByValue, (filter, key) => {
    const valueDynamicFilterItem = valueDynamicFilter[key]
    if (valueDynamicFilterItem) {
      promises.push(
        API.get(filter.remoteUrl, {
          params: {
            ids: valueDynamicFilterItem.value
          }
        }).then(res => ({ key, filter, data: _.map(res.data, (item) => filter.remoteUrlFormatter(item)) }))
      )
    }
  })
  if (promises.length === 0) {
    return Promise.resolve({ optionsData: {}, relationFilterKeys: {} })
  }
  return Promise.all(promises).then(results => {
    const optionsData = {}
    const relationFilterKeys = {}
    results.forEach(({ key, filter: option, data }) => {
      optionsData[key] = data
      relationFilterKeys[key] = _getRelationFilterKeys({ option, data})
    })
    return { optionsData, relationFilterKeys }
  })
}

export const _fetchDropdownButtonRelationsOptions = (relations) => {
  const fetchPromises = _.map(relations, (relation, key) => {
    if (relation.remoteUrl) {
      return API.get(relation.remoteUrl)
        .then(({data}) => ({ key, options: data }))
    }
    return Promise.resolve({ key, options: [] })
  })
  return Promise.all(fetchPromises).then(results => {
    const optionsMap = {}
    results.forEach(({ key, options }) => (optionsMap[key] = options))
    return { relations, options: optionsMap }
  })
}

export const _loadAdvancedDropdownButtonRelationsOptions = (filters) => {
  const relations = {}
  filters.forEach(filter => {
    if (filter.relations) Object.assign(relations, filter.relations)
  })
  if (_.size(relations)) {
    return _fetchDropdownButtonRelationsOptions(relations)
  } else {
    return Promise.resolve({ relations: {}, options: {} })
  }
}

export const _fillDropdownButtonOptionsWithRelations = (filters, relationsArgs) => {
  return _.flatMap(filters, filter => {
    const {value, label, type, relations, ...rest} = filter
    const hasRelations = _.size(relations || {}) > 0
    return [
      { value, label: trans(label), type, has_relations: hasRelations, relations, ...rest },
      ..._.flatMap(relations || {}, (relation, key) => (
        _.map(relationsArgs.options[key] || [], option => relation.remoteUrlFormatter(option))
      ))
    ]
  })
}

export const _getRelationFilterKeys = ({option, data: selectedOptions}) => {
  let keys = []
  _.each(option.relations, relation => {
    const selectedOptionsFields = _.flatMap(selectedOptions, (o) => o[relation.key] || [])
    const selectedOptionsFieldsFormatted = _.map(selectedOptionsFields, item => relation.remoteUrlFormatter(item))
    const selectedOptionsFieldsValues = _.map(selectedOptionsFieldsFormatted, item => item.value)
    keys = [...keys, ...selectedOptionsFieldsValues]
  })
  return keys
}

export const _applyRelationFilter = ({ keys, options, value }) => {
  if (_.size(keys) > 0) {
    const filteredOptions = _.pickBy(options, (o, k) => !_.includes(o.value, '->') || _.includes(keys, o.value))
    const filteredValue = _.pickBy(value, (v, k) => !_.includes(k, '->') || _.includes(keys, k))
    return {options: filteredOptions, value: filteredValue}
  } 
  if (_.size(keys) === 0) {
    const filteredOptions = _.pickBy(options, (o, k) => !_.includes(o.value, '->'))
    const filteredValue = _.pickBy(value, (v, k) => !_.includes(k, '->'))
    return {options: filteredOptions, value: filteredValue}
  }
}

export const _checkRelationDataOnSubmit = ({ relationFilter, option, data }) => {
  if (_.includes(['select', 'asyncSelect'], option.type)) {
    if (option.has_relations) {
      const selectedOptions = data.options
      const relationFilterKeys = _getRelationFilterKeys({option, data: selectedOptions})
      if (_.size(selectedOptions) > 0) {
        return {...relationFilter, [option.value]: relationFilterKeys}
      } 
      if (_.size(selectedOptions) === 0) {
        return _.omit(relationFilter, option.value)
      }
    }
  }
  return relationFilter
}

export const _prepareSimpleAdvancedValue = ({ value, remoteUrl, remoteUrlFormatter }) => {
  return new Promise((resolve) => {
    if (_.size(value) > 0) {
      _loadSimpleOptions({ ids: value, remoteUrl, remoteUrlFormatter }).then(value => {
        resolve(value)
      })
    } else {
      resolve([])
    }
  })
}

export const _prepareOnChangeSimpleAdvancedValue = ({ options }) => {
  return _.size(options) > 0 ? options : null
}

export const _prepareOnChangeAdvancedFilterValue = (entity, { value, options }) => {
  const optionsKeyByValue = _.keyBy(options, "value")
  const selectedOptions = _.map(value, (v, k) => optionsKeyByValue[k])
  const selectedOptionsKeyByValue = _.keyBy(selectedOptions, "value")
  const optionsLabelsKeyByValue = _.reduce(value, (acc, v, idx) => {
    if (_.size(v.options) > 0) {
      acc[idx] = v.options
    }
    return acc
  }, {})
  const dynamic_filter = _.map(value, ({ operator, value, not }, key) => {
    return { key, operator, value, not }
  })
  return {
    query: true,
    entity,
    dynamic_filter,
    selected_options: selectedOptionsKeyByValue,
    options_labels: optionsLabelsKeyByValue
  }
}
